import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Attendance,
  AttendanceQuery,
  AttendancesQueryBuilder,
  BulkDeleteAttendancesResponse,
  BulkSetAttendanceApplicationErrors,
  BulkSetAttendanceOptions,
  BulkSetAttendanceResponse,
  CountAttendancesOptions,
  CountAttendancesResponse,
  DeleteAttendanceApplicationErrors,
  GetAttendanceApplicationErrors,
  QueryAttendanceResponse,
  SetAttendanceApplicationErrors,
  SetAttendanceOptions,
  SetAttendanceResponse,
  bulkDeleteAttendances as universalBulkDeleteAttendances,
  bulkSetAttendance as universalBulkSetAttendance,
  countAttendances as universalCountAttendances,
  deleteAttendance as universalDeleteAttendance,
  getAttendance as universalGetAttendance,
  queryAttendance as universalQueryAttendance,
  setAttendance as universalSetAttendance,
  typedQueryAttendance as universalTypedQueryAttendance,
} from './bookings-v2-attendance-attendance.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function getAttendance(httpClient: HttpClient): GetAttendanceSignature {
  return (attendanceId: string) =>
    universalGetAttendance(
      attendanceId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAttendanceSignature {
  /**
   * Retrieves attendance information.
   * @param - ID of the attendance object to retrieve.
   * @returns Retrieved attendance.
   */
  (attendanceId: string): Promise<
    NonNullablePaths<Attendance, `status` | `numberOfAttendees`, 2> & {
      __applicationErrorsType?: GetAttendanceApplicationErrors;
    }
  >;
}

export function setAttendance(httpClient: HttpClient): SetAttendanceSignature {
  return (
    attendance: NonNullablePaths<Attendance, `bookingId` | `status`, 2>,
    options?: SetAttendanceOptions
  ) =>
    universalSetAttendance(
      attendance,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetAttendanceSignature {
  /**
   * Sets or updates attendance information for a booking session. This
   * information is stored in an `attendance` object.
   *
   * If an `attendance` object already exists for the session, it's updated.
   * Otherwise, a new object is created.
   *
   * By default, `numberOfAttendees` is set to `1`, but you can specify a higher
   * number if multiple participants attended. Do not set `numberOfAttendees` to
   * `0` to indicate no attendance, instead specify `{"status": "NOT_ATTENDED"}`.
   *
   * Validation guidelines:
   *
   * + The call succeeds for mismatches between `numberOfAttendees`
   * and `status`. For example, make sure that your code doesn't specify
   * `{"status": "NOT_ATTENDED"}` with `{"numberOfAttendees": 5}`.
   * + The API also allows `numberOfAttendees` to exceed the booking's
   * `numberOfParticipants`. Use higher values only when scenarios like
   * walk-ins justify the exception.
   * @param - Attendance to create or update.
   * @param - Options to use when setting an attendance.
   */
  (
    attendance: NonNullablePaths<Attendance, `bookingId` | `status`, 2>,
    options?: SetAttendanceOptions
  ): Promise<
    NonNullablePaths<
      SetAttendanceResponse,
      `attendance.status` | `attendance.numberOfAttendees`,
      3
    > & {
      __applicationErrorsType?: SetAttendanceApplicationErrors;
    }
  >;
}

export function bulkSetAttendance(
  httpClient: HttpClient
): BulkSetAttendanceSignature {
  return (options?: BulkSetAttendanceOptions) =>
    universalBulkSetAttendance(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkSetAttendanceSignature {
  /**
   * Sets or updates attendance information for multiple booking sessions.
   *
   *
   * Refer to Set Attendance for detailed behavior of individual attendance
   * entries.
   *
   * The call fails entirely if any entry in `attendanceDetails` is missing a
   * required field.
   *
   * If attendance details are provided for a non-existent session, the call
   * succeeds for valid sessions while marking the unavailable session as a
   * failure in the response.
   * @param - Options to use when setting multiple attendances in bulk.
   */
  (options?: BulkSetAttendanceOptions): Promise<
    NonNullablePaths<
      BulkSetAttendanceResponse,
      | `results`
      | `results.${number}.item.status`
      | `results.${number}.item.numberOfAttendees`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkSetAttendanceApplicationErrors;
    }
  >;
}

export function queryAttendance(
  httpClient: HttpClient
): QueryAttendanceSignature {
  return () =>
    universalQueryAttendance(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAttendanceSignature {
  /**
   * Creates a query to retrieve a list of attendances.
   *
   * The `queryAttendances()` function builds a query to retrieve a list of attendances and returns a `AttendancesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to call the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/bookings/attendance/attendances-query-builder/find) function.
   *
   * You can refine the query by chaining `AttendancesQueryBuilder` functions onto the query. `AttendancesQueryBuilder` functions enable you to sort, filter, and control the results that `queryAttendances()` returns.
   *
   * `queryAttendances()` uses the following `AttendancesQueryBuilder` default values that you can override:
   *
   * + `limit` is `50`.
   * + Sorted by `id` in ascending order.
   *
   * The functions that are chained to `queryAttendances()` are applied in the order they are called. For example, if you apply `ascending("status")` and then `ascending("numberOfAttendees")`, the results are sorted first by the `"status"`, and then, if there are multiple results with the same `"status"`, the items are sorted by `"numberOfAttendees"`.
   *
   * The following `AttendancesQueryBuilder` functions are supported for the `queryAttendances()` function. For a full description of the tip settings object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/bookings/attendance/attendances-query-result/items) property in `AttendancesQueryResult`.
   */
  (): AttendancesQueryBuilder;
}

export function typedQueryAttendance(
  httpClient: HttpClient
): TypedQueryAttendanceSignature {
  return (query: AttendanceQuery) =>
    universalTypedQueryAttendance(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryAttendanceSignature {
  /** @returns List of objects that contain attendance information. */
  (query: AttendanceQuery): Promise<
    NonNullablePaths<
      QueryAttendanceResponse,
      | `attendances`
      | `attendances.${number}.status`
      | `attendances.${number}.numberOfAttendees`,
      4
    >
  >;
}

export function countAttendances(
  httpClient: HttpClient
): CountAttendancesSignature {
  return (options?: CountAttendancesOptions) =>
    universalCountAttendances(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountAttendancesSignature {
  /**
   * Counts attendance records by contact.
   *
   * Returns the total number of attendance records for the contact,
   * with optional filtering by attendance status and service IDs.
   *
   * Filtering options:
   * + **attendance_status**: Filter by attendance status (ATTENDED, NOT_ATTENDED)
   * + **service_id**: Filter by one or more service IDs from the bookings
   *
   * If no filters are provided, returns the total count of all attendance records
   * for the contact.
   */
  (options?: CountAttendancesOptions): Promise<
    NonNullablePaths<CountAttendancesResponse, `count`, 2>
  >;
}

export function deleteAttendance(
  httpClient: HttpClient
): DeleteAttendanceSignature {
  return (attendanceId: string) =>
    universalDeleteAttendance(
      attendanceId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAttendanceSignature {
  /**
   * Deletes an attendance record.
   *
   *
   * This permanently removes the attendance information for a booking session.
   * To recreate the attendance information, call [Set Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/set-attendance).
   *
   * To delete multiple attendance records in a single call, call
   * [Bulk Delete Attendances](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/bulk-delete-attendances).
   * @param - ID of the attendance record to delete.
   */
  (attendanceId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteAttendanceApplicationErrors;
    }
  >;
}

export function bulkDeleteAttendances(
  httpClient: HttpClient
): BulkDeleteAttendancesSignature {
  return (attendanceIds: string[]) =>
    universalBulkDeleteAttendances(
      attendanceIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteAttendancesSignature {
  /**
   * Deletes multiple attendance records.
   *
   *
   * This permanently removes the attendance information for multiple booking sessions.
   * To recreate the attendance information, call [Set Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/set-attendance) or
   * [Bulk Set Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/bulk-set-attendance).
   *
   * To delete a single attendance record, call [Delete Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/delete-attendance).
   * @param - IDs of the attendance records to delete.
   */
  (attendanceIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteAttendancesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  ApplicationError,
  Attendance,
  AttendanceDetails,
  AttendanceMarkedAsNotAttended,
  AttendanceQuerySpec,
  AttendanceStatus,
  AttendancesQueryBuilder,
  AttendancesQueryResult,
  BulkActionMetadata,
  BulkAttendanceResult,
  BulkDeleteAttendancesRequest,
  BulkDeleteAttendancesResponse,
  BulkDeleteAttendancesResult,
  BulkSetAttendanceOptions,
  BulkSetAttendanceRequest,
  BulkSetAttendanceResponse,
  CountAttendancesOptions,
  CountAttendancesRequest,
  CountAttendancesResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteAttendanceRequest,
  DeleteAttendanceResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GetAttendanceRequest,
  GetAttendanceResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  Paging,
  ParticipantNotification,
  QueryAttendanceRequest,
  QueryAttendanceResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  SetAttendanceOptions,
  SetAttendanceRequest,
  SetAttendanceResponse,
  SortOrder,
  Sorting,
  WebhookIdentityType,
  utils,
} from './bookings-v2-attendance-attendance.universal.js';
